import {ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, inject} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {AppVersionService} from './app-version.service';
import {InstallAppComponent} from './install-app.component';
import {ApiService} from './api.service';
import {AuthSessionService} from './auth-session.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-root',
  imports: [RouterOutlet, InstallAppComponent],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements OnInit, OnDestroy {
  readonly version = inject(AppVersionService);
  readonly session = inject(AuthSessionService);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private shiftSessionTimer: ReturnType<typeof setInterval> | undefined;
  private sessionRefreshInFlight = false;

  ngOnInit() {
    this.version.start();
    this.renewValidSession();
    this.api.pingAppDevice('startup');
    this.shiftSessionTimer = setInterval(() => this.refreshActiveShiftSession(), 30 * 60 * 1000);
  }

  ngOnDestroy() {
    if (this.shiftSessionTimer) clearInterval(this.shiftSessionTimer);
  }

  @HostListener('window:focus')
  onWindowFocus() {
    this.version.check();
    this.renewValidSession();
    this.api.pingAppDevice('focus');
    this.refreshActiveShiftSession();
  }

  signInAgain() {
    this.api.logout();
    this.router.navigate(['/login']);
  }

  @HostListener('window:storage', ['$event'])
  onStorageChange(event: StorageEvent) {
    if (event.key !== this.api.sessionStorageKey) return;
    this.api.syncSessionFromStorage();
    if (!this.api.isAuthenticated()) this.router.navigate(['/login']);
  }

  private renewValidSession() {
    if (this.sessionRefreshInFlight || this.session.expired() || !this.api.isAuthenticated()) return;
    this.sessionRefreshInFlight = true;
    this.api.refreshSession().subscribe({
      next: () => this.sessionRefreshInFlight = false,
      error: () => this.sessionRefreshInFlight = false
    });
  }

  private refreshActiveShiftSession() {
    if (!this.api.currentShiftActive() || this.session.expired()) return;
    this.api.getShiftStatus().subscribe({ error: () => undefined });
  }
}
